import React, { FC, useEffect, useState } from 'react';
import cx from 'classnames';
import { compact } from 'lodash';
import { PropsWithClassName } from '@/types/common';
import { preloadImage } from '@/utils/misc';
import Loading from '@/components/Common/Loading';
import { ConfiguredProduct, ConfiguredProductBundle } from '@/types/index';
import { isConfiguredBundle } from '@/utils/configurator';
import * as styles from './ConfiguredProductPreview.module.scss';

type Image = string;
type ImageWithFallback = [main: Image, fallback: Image];

const baseUrl = 'https://media.kettal.es/product_configurator';

const makeUrl = (...parts: string[]) =>
  [baseUrl, ...parts.filter((part) => !!part)].join('/');

const makeProductCharacteristicImageUrl = (
  sku: string,
  index: number,
  code: string
): Image => {
  const dir = `C${index + 1}`;
  return makeUrl(sku, dir, `${code}.png`);
};

const makeProductBundleCharacteristicImageUrl = (
  parent: string,
  child: string,
  index: number,
  code: string
): ImageWithFallback => {
  const dir = `C${index + 1}`;
  return [
    makeUrl(parent, dir, child, `${code}.png`),
    makeUrl(parent, dir, `${code}.png`),
  ];
};

const makeChildBaseImageUrl = (parent: string, child: string): Image =>
  makeUrl(parent, child, 'background.jpg');

const makeParentBaseImageUrl = (parent: string): Image =>
  makeUrl(parent, 'background.jpg');

const makeProductImageUrl = (product: ConfiguredProduct) => {
  const base = makeParentBaseImageUrl(product.sku);
  const characteristics = product.characteristics.map((code, index) =>
    code ? makeProductCharacteristicImageUrl(product.sku, index, code) : undefined
  );
  return [base, ...compact(characteristics)];
};

const makeProductBundleImageUrls = (bundle: ConfiguredProductBundle) => {
  const base = makeParentBaseImageUrl(bundle.sku);
  const options = bundle.options
    .map((option) =>
      compact([
        option?.sku ? makeChildBaseImageUrl(bundle.sku, option.sku) : undefined,
        ...(option?.characteristics?.map((code, index) =>
          code
            ? makeProductBundleCharacteristicImageUrl(bundle.sku, option.sku, index, code)
            : undefined
        ) ?? []),
      ])
    )
    .flat() as (Image | ImageWithFallback)[];
  return [base, ...options];
};

const preloadImageWithFallback = async (images: ImageWithFallback) => {
  const [main, fallback] = images;
  return (await preloadSingleImage(main)) ?? preloadSingleImage(fallback);
};

const preloadSingleImage = async (image: Image) => {
  try {
    await preloadImage(image);
    return image;
  } catch (e) {
    return null;
  }
};

type Props = PropsWithClassName<{
  product: ConfiguredProductBundle | ConfiguredProduct;
}>;

const ConfiguredProductPreview: FC<Props> = ({ className, product }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [existingUrls, setExistingUrls] = useState<string[]>([]);

  const images: (Image | ImageWithFallback)[] = isConfiguredBundle(product)
    ? makeProductBundleImageUrls(product)
    : makeProductImageUrl(product);

  const handlePreloadImages = async () => {
    setIsLoading(false);
    const urls = await Promise.all(
      images.map((image) =>
        Array.isArray(image) ? preloadImageWithFallback(image) : preloadSingleImage(image)
      )
    );
    setExistingUrls(compact(urls));
    setIsLoading(false);
  };

  useEffect(() => {
    handlePreloadImages();
  }, [product]);

  return (
    <div className={cx(styles.main, className)}>
      {isLoading ? <Loading className={styles.loading} /> : null}
      <div className={styles.images}>
        {existingUrls.map((url) => (
          <img className={styles.image} key={url} src={url} alt="" />
        ))}
      </div>
    </div>
  );
};

export default ConfiguredProductPreview;
